'use client';
import React, { useEffect, useState, useMemo } from 'react';
import { StyledIndustryContainer } from '@/components/common/ygen/sections/industry/industry-container.style';
import { StyledIndustry } from '@/components/common/ygen/sections/industry/industry.style';
import { Col, Container, Row } from 'react-bootstrap';
import Title from '@/components/common/ui/text/Title';
import Button from '@/components/common/ui/buttons/Button';
import LeftSVGScroll from '@/components/common/ui/buttons/LeftSVGScroll';
import { Swiper, SwiperSlide } from 'swiper/react';
import IndustrySingle from '@/components/common/ui/single-item/IndustrySingle';
import { Mousewheel, Pagination } from 'swiper/modules';
import useContainerOffset from '@/components/hooks/useContainerOffset';
import useDeviceTypeMobile from '@/components/hooks/useDeviceTypeMobile';
import Prev from '@/components/common/ui/buttons/Prev';
import Next from '@/components/common/ui/buttons/Next';
import BannerAdsnoSection from '@/components/common/ygen/sections/banner-ads-no-section/BannerAdsnoSection';
import RightSVGScroll from '@/components/common/ui/buttons/RightSVGScroll';
import reactHtmlParser from 'react-html-parser';
import { usePathname } from 'next/navigation';


const Industry = ({ data, version, color, mainIndex, getIndustryDataALL, getImpactStoriesDataALL, getSolutionDataALL }) => {
	const isMobile = useDeviceTypeMobile();
	const offset = useContainerOffset('.container');
	const pathName = usePathname()

	// Memoize the data to prevent unnecessary re-renders
	const [getIndustryData, setIndustryData] = useState(() => getIndustryDataALL);
	const [getImpactData, setImpactData] = useState(() => getImpactStoriesDataALL);
	const [getSolutionData, setSolutionData] = useState(() => getSolutionDataALL);


	// Memoize the data update effect
	useEffect(() => {
		let mounted = true;

		const updateData = () => {
			if (!mounted) return;

			if (getIndustryDataALL) {
				setIndustryData(getIndustryDataALL);
			}
			if (getImpactStoriesDataALL) {
				setImpactData(getImpactStoriesDataALL);
			}
			if (getSolutionDataALL) {
				setSolutionData(getSolutionDataALL);
			}
		};

		updateData();

		return () => {
			mounted = false;
		};
	}, []);

	// Memoize the subtitle and description to prevent unnecessary re-renders
	const subtitle = useMemo(() => (
		data?.section_data?.subtitle || 'Real Results, Proven Success'
	), [data?.section_data?.subtitle]);

	const description = useMemo(() => (
		data?.section_data?.description || 'See real-world examples of how our <i>solutions</i> transform businesses and <i>drive growth</i>.'
	), [data?.section_data?.description]);

	// Memoize the button props to prevent unnecessary re-renders
	const buttonProps = useMemo(() => ({
		color: color ? color :  '#FFFFFF',
		hoverBackground:  undefined,
		hoverColor: undefined,
		background:  '#FFFFFF',
		pathColor:  '#152637',
		text:  'View All Industries',
		src: data?.section_data?.short_desc || '/industries-empowered'
	}), [version, data?.section_data?.short_desc]);

	// Memoize the Swiper breakpoints to prevent unnecessary re-renders
	const swiperBreakpoints = useMemo(() => ({
		320: {
			slidesPerView: 1.2,
			spaceBetween: 30,
			mousewheel: false,
			navigation: {
				prevEl: version === 2 || version === 3 ? '#case-study-prev' : '#industry-prev',
				nextEl: version === 2 || version === 3 ? '#case-study-next' : '#industry-next',
			},
		},
		768: {
			slidesPerView: 3.5,
			spaceBetween: 30,
		},
		1024: {
			slidesPerView: 4.2,
			spaceBetween: 30,
		},
	}), [version]);

	// Memoize the mousewheel config to prevent unnecessary re-renders
	const mousewheelConfig = useMemo(() => ({
		releaseOnEdges: true,
		sensitivity: 1.5,
		thresholdDelta: 1.5,
		thresholdTime: 5,
		eventsTarget: `.industry-scroll-wrapper-main-${mainIndex}`,
	}), [mainIndex]);
	// Memoize the container style to prevent unnecessary re-renders

	return (
		<StyledIndustryContainer data-scroll-section offset={offset} className={`main-industry industry-scroll-wrapper-main-${mainIndex} ${version === 2 ? 'version_two' : ''} ${version === 3 ? 'version_three' : ''}`}>
			<StyledIndustry id="okay-hero" offset={offset} className={`${version === 2 ? 'version_two' : ''} ${version === 3 ? 'version_three' : ''} industry_footprintss`}>
				<div className={`industry-container ${version === 2 ? 'industry-container-scroll' : ''}`}>
					{version === 3 ? <RightSVGScroll version /> : <LeftSVGScroll />}

					<Container>
						<Row className={'mb80'}>
							<Col md={3}>
								<p data-scroll data-scroll-repeat className={'subtitle-ygen fade-up'}>
									{subtitle}
								</p>
							</Col>
							<Col md={6}>
								<Title
									tag={'h2'}
									animClass={'fade-up'}
									text={description}
									color={version === 3 ? '#152637' : '#fff'}
								/>
							</Col>
							<Col md={3} className={'d-flex justify-content-end mobile-justify'}>
								{!isMobile && <Button {...buttonProps} />}
								<div className="d-flex navigation-item">
									<Prev id={version === 2 ? 'case-study-prev' : 'industry-prev'} />
									<Next id={version === 2 ? 'case-study-next' : 'industry-next'} />
								</div>
							</Col>
						</Row>
					</Container>

					<Container fluid={ true} style={{ paddingLeft:  offset + 15 }}>
						<Row>
							{
								getIndustryData?.child_pages?.list?.length > 6 && (
									<Col md={12}>
										<Swiper
											slidesPerView={4.2}
											spaceBetween={30}
											slidesPerGroup={4}
											loop={false}
											autoplay={false}
											speed={1500}
											pagination={false}
											mousewheel={mousewheelConfig}
											navigation={{
												prevEl: '#industry-prev',
												nextEl: '#industry-next',
											}}
											breakpoints={swiperBreakpoints}
											modules={[Mousewheel, Pagination]}
											className="about-slider_mask w-slider-mask"
										>
											{getIndustryData?.child_pages?.list.slice(0,8)?.map((e, index) => {
												const thumb = e?.images?.list?.find((f) => f?.thumb === 'on');
												return (
													<SwiperSlide key={index}>
														<IndustrySingle
															link={`/industries/${e?.page_data?.slug}`}
															img={thumb?.full_path}
															title={reactHtmlParser(e?.page_data?.title)}
															count={index + 1}
														/>
													</SwiperSlide>
												);
											})}
										</Swiper>
									</Col>
								)
							}
						</Row>
					</Container>

					{isMobile && (
						<Container>
							<Row>
								<Col md={12}>
									<Button margin={'30px 0 0'}  {...buttonProps} />
								</Col>
							</Row>
						</Container>
					)}
				</div>
			</StyledIndustry>
			{version !== 2 && version !== 3 && (
				<BannerAdsnoSection data={data?.posts?.list?.[0]?.data} />
			)}
		</StyledIndustryContainer>
	);
};


export default React.memo(Industry);
